import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './MyTasksPage.css';

export default function MyTasksPage() {
  const { t } = useTranslation();
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [completing, setCompleting] = useState(null);

  useEffect(() => {
    axiosInstance.get('/training/my-tasks')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const completeTraining = (id) => {
    setCompleting(id);
    axiosInstance.post(`/training/${id}/complete`)
      .then(r => setData(prev => ({
        ...prev,
        totalPending: prev.totalPending - 1,
        trainingTasks: prev.trainingTasks.map(t => t.id === id ? { ...t, ...r.data } : t),
      })))
      .catch(() => {})
      .finally(() => setCompleting(null));
  };

  const policyTasks   = data?.policyTasks   || [];
  const trainingTasks = data?.trainingTasks  || [];
  const pendingCount  = data?.totalPending   || 0;

  return (
    <div className="mt-page">
      <div className="mt-header">
        <h2>
          <i className="fas fa-tasks" style={{ marginRight: 10, color: '#1d4ed8' }} />
          {t('MyTasksPage.pageTitle')}
        </h2>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          {t('MyTasksPage.pageSubtitle')}
        </div>
        {!loading && (
          <div className={`mt-pending-badge ${pendingCount === 0 ? 'mt-badge-clear' : 'mt-badge-warn'}`}>
            {pendingCount === 0
              ? <><i className="fas fa-check-circle" /> {t('MyTasksPage.allCaughtUp')}</>
              : <><i className="fas fa-exclamation-circle" /> {t('MyTasksPage.tasksPending', { count: pendingCount })}</>}
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-loading"><i className="fas fa-spinner fa-spin" /> {t('MyTasksPage.loading')}</div>
      ) : (
        <>
          {/* Policy Signatures */}
          <div className="mt-section">
            <div className="mt-section-title">
              <i className="fas fa-file-signature" /> {t('MyTasksPage.policySignaturesTitle')}
            </div>
            {policyTasks.length === 0 ? (
              <div className="mt-section-empty">{t('MyTasksPage.policyEmpty')}</div>
            ) : (
              <div className="mt-task-list">
                {policyTasks.map(task => (
                  <div key={task.id} className={`mt-task-card ${task.completed ? 'mt-task-done' : ''}`}>
                    <div className="mt-task-icon mt-icon-policy">
                      <i className="fas fa-file-signature" />
                    </div>
                    <div className="mt-task-body">
                      <div className="mt-task-title">{task.title}</div>
                      <div className="mt-task-detail">{task.detail}</div>
                    </div>
                    <div className="mt-task-status">
                      {task.completed ? (
                        <span className="mt-status-badge mt-done">✓ {t('MyTasksPage.complete')}</span>
                      ) : (
                        <span className="mt-status-badge mt-pending">{t('MyTasksPage.pending')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Training Assignments */}
          <div className="mt-section">
            <div className="mt-section-title">
              <i className="fas fa-graduation-cap" /> {t('MyTasksPage.trainingAssignmentsTitle')}
            </div>
            {trainingTasks.length === 0 ? (
              <div className="mt-section-empty">{t('MyTasksPage.trainingEmpty')}</div>
            ) : (
              <div className="mt-task-list">
                {trainingTasks.map(task => (
                  <div key={task.id} className={`mt-task-card ${task.completed ? 'mt-task-done' : ''}`}>
                    <div className="mt-task-icon mt-icon-training">
                      <i className="fas fa-graduation-cap" />
                    </div>
                    <div className="mt-task-body">
                      <div className="mt-task-title">{task.trainingName}</div>
                      <div className="mt-task-detail">
                        {task.dueDate && (
                          <span>{t('MyTasksPage.dueDate', { date: new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) })} · </span>
                        )}
                        {task.completed
                          ? t('MyTasksPage.completedOn', { date: new Date(task.completedAt).toLocaleDateString() })
                          : t('MyTasksPage.notYetCompleted')}
                      </div>
                      {task.trainingLink && !task.completed && (
                        <a href={task.trainingLink} target="_blank" rel="noopener noreferrer" className="mt-training-link">
                          <i className="fas fa-external-link-alt" /> {t('MyTasksPage.startTraining')}
                        </a>
                      )}
                    </div>
                    <div className="mt-task-status">
                      {task.completed ? (
                        <span className="mt-status-badge mt-done">✓ {t('MyTasksPage.complete')}</span>
                      ) : (
                        <button className="mt-complete-btn"
                                disabled={completing === task.id}
                                onClick={() => completeTraining(task.id)}>
                          {completing === task.id
                            ? <i className="fas fa-spinner fa-spin" />
                            : t('MyTasksPage.markComplete')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
